/* eslint-disable max-lines */
import {
  CellClassParams,
  CellRendererSelectorResult,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { BehaviorSubject } from 'rxjs';

import { AgCollapsibleHeaderComponent } from '@shared/ag-components/ag-collapsible-header/ag-collapsible-header.component';
import {
  AgDatepickerComponent,
  AgDropdownComponent,
  AgInputComponent,
} from '@shared/ag-components/ag-controls';
import { AgInputMaskComponent } from '@shared/ag-components/ag-controls/ag-input-mask.component';
import { AgDotsComponent } from '@shared/ag-components/ag-dots/ag-dots.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { initialPaginationSize } from '@shared/constants/pagination.constants';
import { TableConstants } from '@shared/constants/table.constants';
import {
  Currency,
  InvestigatorTransactionSourceType,
  PatientProtocolType,
} from '@shared/services/gql.service';
import { InvestigatorTransactionsDataRow } from '@shared/services/investigator-transactions/investigator-transaction-form.model';
import { CurrencyToggle, Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import {
  GetTransactionColsArgs,
  OverrideGridOptions,
} from '../models/investigator-transaction-grid.model';

export const CATEGORY_OPTIONS: Option[] = [
  {
    label: 'Patient Visit',
    value: PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT,
  },
  {
    label: 'Patient Invoiceable',
    value: PatientProtocolType.PATIENT_PROTOCOL_OTHER,
  },
  {
    label: 'Site Invoiceable',
    value: PatientProtocolType.PATIENT_PROTOCOL_OVERHEAD,
  },
];

const defaultHeaderClass = [
  'text-center',
  '[&_.ag-header-cell-text]:whitespace-normal',
  '[&_.ag-header-cell-text]:line-clamp-2',
  '[&_.ag-header-cell-text]:w-full',
];
const defaultMinWidth = 56;

const suppressKeyboardEvent = () => true;

const isStaticMode = (mode?: string) => mode === 'static';

const getCellClasses = (
  classes: string[],
  cellClassParams: CellClassParams,
  savedTransactionIds: string[] = []
): string[] => {
  return isStaticMode(cellClassParams.data?.mode) &&
    !savedTransactionIds.includes(cellClassParams.data.id)
    ? classes
    : [...classes, 'flex flex-col !justify-center !items-stretch new-cell'];
};

const cellRerenderWithControl = (
  params: ICellRendererParams,
  control: CellRendererSelectorResult
) => {
  return !isStaticMode(params.data?.mode) ? control : undefined;
};

const tooltipValueGetter =
  (propertyName: keyof InvestigatorTransactionsDataRow) =>
  (params: ITooltipParams): string => {
    return isStaticMode(params?.data?.mode) ? params?.data[propertyName] : '';
  };

// eslint-disable-next-line max-lines-per-function
export const getInvestigatorTransactionCols = ({
  editRow,
  isContractCurrency,
  isEDCColumnBeShown,
  isPatientsFinalized,
  isSiteColumnCollapsed$,
  patientGroups$,
  protocolVersionOptions$,
  removeRow,
  savedTransactionIds$,
  showSiteBudgetVersionColumn,
  showSourceColumn,
  siteOptions,
  transactionMode$,
  updatePatientIdField,
  updateSiteFields,
  updateTransactionPermission,
}: GetTransactionColsArgs): OverrideGridOptions => ({
  ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
  columnDefs: [
    {
      field: 'id',
      hide: true,
    },
    {
      colId: 'sort_to_top_ids',
      field: 'sort_to_top_ids',
      hide: true,
    },
    {
      colId: 'create_date',
      field: 'create_date',
      hide: true,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      cellRendererSelector: (params) =>
        cellRerenderWithControl(params, {
          component: AgDatepickerComponent,
          params: { size: 'compact', validators: 'required' },
        }),
      colId: 'activity_date',
      field: 'activity_date',
      headerClass: defaultHeaderClass,
      headerName: 'Completion Date',
      headerTooltip: 'Completion Date',
      minWidth: defaultMinWidth,
      tooltipValueGetter: tooltipValueGetter('activity_date'),
      width: 130,
    },
    {
      cellClass: (params) =>
        getCellClasses(
          [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
          params,
          savedTransactionIds$.getValue()
        ),
      colId: 'total_cost',
      field: 'total_cost',
      headerClass: defaultHeaderClass,
      headerName: 'Total Cost (USD)',
      headerTooltip: 'Total Cost (USD)',
      minWidth: defaultMinWidth,
      tooltipValueGetter: (params) =>
        Utils.agCurrencyFormatter({ value: params.value } as ValueFormatterParams, Currency.USD),
      valueFormatter: (params) => Utils.agCurrencyFormatter(params, Currency.USD),
      width: 140,
    },
    {
      cellClass: (params) => getCellClasses([], params, savedTransactionIds$.getValue()),
      cellClassRules: {
        [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT]: (params) => params.data?.mode === 'static',
      },
      cellRendererSelector: (params) => {
        const currencySign = Utils.currencyFormatter(1, {}, params.data.contract_curr).replace(
          /\d*(\.)*/gi,
          ''
        );

        return cellRerenderWithControl(params, {
          component: AgInputMaskComponent,
          params: {
            allowNegative: true,
            currencySign,
            placeholder: 'Enter Amount',
            size: 'compact',
          },
        });
      },
      colId: 'contract_amount',
      field: 'contract_amount',
      headerClass: defaultHeaderClass,
      headerName: 'Total Cost (Contract)',
      headerTooltip: 'Total Cost (Contract)',
      hide: !isContractCurrency,
      minWidth: defaultMinWidth,
      tooltipValueGetter: (params) => {
        if (!isStaticMode(params.data?.mode)) {
          return '';
        }

        return Utils.agMultipleCurrencyFormatter(
          new BehaviorSubject<CurrencyToggle>(CurrencyToggle.CONTRACTED)
        )({
          data: {
            ...params.data,
            contractCurrency: params.data.contract_curr,
          },
          value: params.value,
        });
      },
      valueFormatter: (params) =>
        Utils.agMultipleCurrencyFormatter(
          new BehaviorSubject<CurrencyToggle>(CurrencyToggle.CONTRACTED)
        )({
          ...params,
          data: {
            ...params.data,
            contractCurrency: params.data.contract_curr,
          },
        }),
      width: 140,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'contract_curr',
      field: 'contract_curr',
      headerName: 'Contract Currency',
      hide: !isContractCurrency,
      tooltipField: 'contract_curr',
      valueFormatter: Utils.dashFormatter,
      width: 110,
    },
    {
      cellClass: (params) =>
        getCellClasses(
          [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
          params,
          savedTransactionIds$.getValue()
        ),
      colId: 'exchange_rate',
      field: 'exchange_rate',
      headerName: 'Exchange Rate',
      hide: !isContractCurrency,
      tooltipField: 'exchange_rate',
      valueFormatter: Utils.dashFormatter,
      width: 115,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      cellRendererSelector: (params) =>
        cellRerenderWithControl(params, {
          component: AgInputComponent,
          params: {
            placeholder: 'Type here',
            size: 'compact',
          },
        }),
      colId: 'description',
      field: 'description',
      headerClass: defaultHeaderClass,
      headerName: 'Description',
      headerTooltip: 'Description',
      minWidth: defaultMinWidth,
      suppressKeyboardEvent,
      tooltipValueGetter: tooltipValueGetter('description'),
      valueFormatter: Utils.dashFormatter,
      width: 125,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      cellRendererSelector: (params) =>
        cellRerenderWithControl(params, {
          component: AgDropdownComponent,
          params: {
            changeHandler: (option?: { value: string }) => {
              updatePatientIdField(params.data.id, option?.value);
            },
            classes: 'small',
            options: CATEGORY_OPTIONS,
          },
        }),
      colId: 'patient_protocol_category',
      field: 'patient_protocol_category',
      headerClass: defaultHeaderClass,
      headerName: 'Category',
      headerTooltip: 'Category',
      minWidth: defaultMinWidth,
      sortable: true,
      tooltipValueGetter: tooltipValueGetter('patient_protocol_category'),
      valueFormatter: Utils.dashFormatter,
      width: 150,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'site_budget_note',
      field: 'site_budget_note',
      headerClass: defaultHeaderClass,
      headerName: 'Site Budget Notes',
      headerTooltip: 'Site Budget Notes',
      minWidth: defaultMinWidth,
      sortable: false,
      tooltipValueGetter: tooltipValueGetter('site_budget_note'),
      valueFormatter: Utils.dashFormatter,
      width: 105,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      cellRendererSelector: (params) =>
        cellRerenderWithControl(params, {
          component: AgDropdownComponent,
          params: {
            changeHandler: (option?: { value: string }) => {
              updateSiteFields(params.data.id, option?.value);
            },
            classes: 'small',
            options: siteOptions().map(({ subTitle, title, value }) => ({
              label: title,
              subTitle,
              value,
            })),
            searchable: true,
          },
        }),
      colId: 'site_no',
      field: 'site_no',
      headerClass: defaultHeaderClass,
      headerComponent: AgCollapsibleHeaderComponent,
      headerComponentParams: {
        collapsedByDefault: true,
        collapsibleColumns: ['site_name', 'site_address', 'country', 'investigator_name'],
        isColumnCollapsed$: isSiteColumnCollapsed$,
        shouldAutoSize: false,
      },
      headerName: 'Site #',
      headerTooltip: 'Site #',
      minWidth: 70,
      sortable: true,
      tooltipValueGetter: tooltipValueGetter('site_no'),
      valueFormatter: Utils.dashFormatter,
      width: 95,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'site_name',
      field: 'site_name',
      headerClass: defaultHeaderClass,
      headerName: 'Site Name',
      headerTooltip: 'Site Name',
      hide: true,
      minWidth: defaultMinWidth,
      sortable: true,
      tooltipField: 'site_name',
      valueFormatter: Utils.dashFormatter,
      width: 85,
    },
    {
      cellClass: (params) =>
        getCellClasses(
          ['text-left', 'cell-left', 'whitespace-nowrap overflow-hidden text-ellipsis !block'],
          params,
          savedTransactionIds$.getValue()
        ),
      colId: 'site_address',
      field: 'site_address',
      headerClass: defaultHeaderClass,
      headerName: 'Site Address',
      headerTooltip: 'Site Address',
      hide: true,
      minWidth: defaultMinWidth,
      sortable: true,
      tooltipField: 'site_address',
      valueFormatter: Utils.dashFormatter,
      width: 105,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'country',
      field: 'country',
      headerClass: defaultHeaderClass,
      headerName: 'Country',
      headerTooltip: 'Country',
      hide: true,
      minWidth: defaultMinWidth,
      sortable: true,
      tooltipField: 'country',
      valueFormatter: Utils.dashFormatter,
      width: 100,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'investigator_name',
      field: 'investigator_name',
      headerClass: defaultHeaderClass,
      headerName: 'Primary Investigator',
      headerTooltip: 'Primary Investigator',
      hide: true,
      minWidth: defaultMinWidth,
      sortable: true,
      tooltipField: 'investigator_name',
      valueFormatter: (params) => {
        return Utils.dashFormatter({
          ...params,
          value: params.value?.trim(),
        });
      },
      width: 130,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      cellRendererSelector: (params) =>
        cellRerenderWithControl(params, {
          component: AgInputComponent,
          params: {
            placeholder: 'Type here',
            size: 'compact',
          },
        }),
      colId: 'external_patient_id',
      field: 'external_patient_id',
      headerClass: defaultHeaderClass,
      headerName: 'Patient ID',
      headerTooltip: 'Patient ID',
      minWidth: defaultMinWidth,
      tooltipValueGetter: tooltipValueGetter('external_patient_id'),
      valueFormatter: Utils.dashFormatter,
      width: 95,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      cellRendererSelector: (params) =>
        cellRerenderWithControl(params, {
          component: AgDropdownComponent,
          params: {
            classes: 'small',

            options: patientGroups$.getValue().map(({ id, name }) => ({ label: name, value: id })),
          },
        }),
      colId: 'patient_group_id',
      field: 'patient_group_id',
      headerClass: defaultHeaderClass,
      headerName: 'Patient Group',
      headerTooltip: 'Patient Group',
      minWidth: defaultMinWidth,
      tooltipValueGetter: tooltipValueGetter('patient_group_id'),
      valueFormatter: Utils.dashFormatter,
      width: 95,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      cellRendererSelector: (params) =>
        cellRerenderWithControl(params, {
          component: AgDropdownComponent,
          params: {
            classes: 'small',
            options: protocolVersionOptions$
              .getValue()
              .map(({ id, name }) => ({ label: name, value: id })),
          },
        }),
      colId: 'patient_protocol_version_name',
      field: 'patient_protocol_version_name',
      headerClass: defaultHeaderClass,
      headerName: 'Protocol Version',
      headerTooltip: 'Protocol Version',
      minWidth: defaultMinWidth,
      sortable: true,
      tooltipValueGetter: tooltipValueGetter('patient_protocol_version_name'),
      valueFormatter: Utils.dashFormatter,
      width: 105,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'site_budget_version_name',
      field: 'site_budget_version_name',
      headerClass: defaultHeaderClass,
      headerName: 'Site Budget Version',
      headerTooltip: 'Site Budget Version',
      hide: !showSiteBudgetVersionColumn,
      minWidth: defaultMinWidth,
      sortable: false,
      tooltipValueGetter: tooltipValueGetter('site_budget_version_name'),
      valueFormatter: Utils.dashFormatter,
      width: 105,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'source_name',
      field: 'source_name',
      headerClass: defaultHeaderClass,
      headerName: 'Source',
      headerTooltip: 'Source',
      hide: !showSourceColumn,
      minWidth: defaultMinWidth,
      sortable: true,
      tooltipField: 'Source',
      valueFormatter: Utils.dashFormatter,
      width: 95,
    },
    {
      cellClass: (params) =>
        getCellClasses(['text-left', 'cell-left'], params, savedTransactionIds$.getValue()),
      colId: 'source_created_date',
      field: 'source_created_date',
      headerClass: defaultHeaderClass,
      headerName: 'Created Date',
      headerTooltip: 'Created Date',
      hide: !isEDCColumnBeShown,
      minWidth: defaultMinWidth,
      tooltipField: 'EDCCreatedDate',
      valueFormatter: Utils.dashFormatter,
      width: 110,
    },
    {
      cellClass: (params) => {
        const classes = getCellClasses([], params, savedTransactionIds$.getValue());

        classes.push('!p-0');

        return classes;
      },
      cellRenderer: AgDotsComponent,
      cellRendererParams: (params: ICellRendererParams) => {
        const sourceType = params.data.source_type;
        const createMode = params.data.mode === 'create';
        const staticMode = params.data.mode === 'static';

        const disabled =
          sourceType !== InvestigatorTransactionSourceType.INVESTIGATOR_TRANSACTION_SOURCE_MANUAL ||
          (transactionMode$.getValue() === 'create' && staticMode);

        const options = [];

        if (
          sourceType !==
            InvestigatorTransactionSourceType.INVESTIGATOR_TRANSACTION_SOURCE_SITE_INVOICEABLE_SCHEDULE &&
          !createMode
        ) {
          options.push({
            onClick: () => {
              const id = params.data.id;

              if (id) {
                editRow(id);
              }
            },
            title: 'Edit',
          });
        }

        let tooltip = '';

        if (
          sourceType !== InvestigatorTransactionSourceType.INVESTIGATOR_TRANSACTION_SOURCE_MANUAL
        ) {
          tooltip = 'In-App editing is not supported for this investigator transaction source.';
        } else if (
          !updateTransactionPermission() &&
          sourceType === InvestigatorTransactionSourceType.INVESTIGATOR_TRANSACTION_SOURCE_MANUAL
        ) {
          tooltip = MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
        } else if (
          isPatientsFinalized() &&
          sourceType === InvestigatorTransactionSourceType.INVESTIGATOR_TRANSACTION_SOURCE_MANUAL
        ) {
          tooltip = MessagesConstants.PATIENT_TRACKER_CLOSED;
        }

        return {
          disabled: disabled || isPatientsFinalized() || !updateTransactionPermission(),
          options: [
            ...options,
            {
              onClick: () => {
                const id = params.data.id;

                if (id) {
                  void removeRow(id, createMode);
                }
              },
              optionClass: 'text-aux-red-dark',
              title: 'Delete',
            },
          ],
          tooltip,
        };
      },
      field: 'dots',
      headerClass: defaultHeaderClass,
      headerName: '',
      headerTooltip: '',
      minWidth: 40,
      resizable: false,
      sortable: false,
      width: 40,
    },
  ],
  defaultColDef: {
    ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
    minWidth: 100,
    resizable: true,
    suppressKeyboardEvent,
    suppressSizeToFit: true,
  },
  paginationPageSize: initialPaginationSize(LocalStorageKey.PAGINATION_INVESTIGATOR_TRANSACTIONS),
});
